import { isNumber } from 'lodash';
import { useCallback } from 'react';
import { Cell } from 'src/components/organisms/DataTable/types';
import { Inheritance, Module, TableOverviewRow } from 'src/domain';
import { mergeOverviewRows } from 'src/utils/mergeRows';
import { useSetSelectedInheritance } from '../inheritance/useSetSelectedInheritance';
import { useUnsavedChangesModule } from '../unsaved-changes';
import { useActiveMonth } from '../useActiveMonth';
import { useGenderProductLineKey } from '../useGenderProductLineKey';
import { useMarkupOverviewSimulationRows } from './simulation/useMarkupOverviewSimulationRows';

export const useMarkupHandleCellChanges = () => {
    const [unsavedChanges, setUnsavedChanges] = useUnsavedChangesModule<Record<string, TableOverviewRow[]>>(
        Module.Markup
    );

    const { data: typedData } = useMarkupOverviewSimulationRows(Inheritance.Typed) ?? {};
    const { data: originalData } = useMarkupOverviewSimulationRows();

    const setSelectedInheritance = useSetSelectedInheritance();

    const genderProductLineKey = useGenderProductLineKey();

    const [activeMonth] = useActiveMonth();

    const callback = useCallback(
        (cells: Cell[]) => {
            if (!typedData || !originalData) {
                throw new Error('Data is missing!');
            }

            const mergedRows = mergeOverviewRows(typedData, originalData);

            cells.forEach((cellValue) => {
                const rowIndexToUpdate = mergedRows.findIndex((x) => x.id === Number(cellValue.rowId));
                const fieldName =
                    !cellValue.column || cellValue.column === 'plannedValue'
                        ? activeMonth
                        : cellValue.column?.split('.')[1];

                const valueIsValid = !Number.isNaN(cellValue.value) && isNumber(Number(cellValue.value));

                if (fieldName) {
                    mergedRows[rowIndexToUpdate] = {
                        ...mergedRows?.[rowIndexToUpdate],
                        columns: {
                            ...mergedRows?.[rowIndexToUpdate].columns,
                            [fieldName]: valueIsValid ? cellValue.value : 0,
                        },
                    };
                }
            });

            const unsaved = {
                ...unsavedChanges,
                ...{ [genderProductLineKey]: mergedRows },
            };

            setUnsavedChanges(unsaved);
            setSelectedInheritance(Inheritance.Typed);
        },
        [
            typedData,
            originalData,
            unsavedChanges,
            genderProductLineKey,
            setUnsavedChanges,
            setSelectedInheritance,
            activeMonth,
        ]
    );

    return callback;
};
